import { FC } from 'react';
import { describeModel, ModelCatalog, ModelIdentity } from '@/utils/modelIdentity';

interface Props {
  /** Identidade já resolvida pelo servidor (`model_info`). Fonte preferida: não depende de
   *  segunda chamada nem de catálogo no cliente. */
  info?: ModelIdentity | null;
  /** Id do deployment. Usado só quando `info` não veio (compat / render otimista). */
  model?: string | null;
  catalog?: ModelCatalog | null;
  /** `header` = chip no topo do chat; `inline` = crédito discreto no pé da resposta. */
  variant?: 'header' | 'inline';
  className?: string;
}

// Sem absolutismo: a inferência roda no nosso Azure, mas a busca na web (quando usada) manda a
// consulta pra fora. A ressalva completa está na base de ajuda e no prompt do bot.
const ONDE_RODA = 'A conversa é processada na infraestrutura da h.company no Microsoft Azure.';

/**
 * Diz qual IA está respondendo, em TEXTO.
 *
 * Por decisão de produto não usamos o logotipo do fornecedor: o nome escrito cumpre o
 * mesmo papel ("é a mesma IA que você usa fora") sem depender das regras de uso de marca
 * de terceiro. O ícone é o nosso, genérico.
 *
 * Não é interativo de propósito. Existia aqui um `<select>` de modelo que o backend
 * ignorava: o usuário trocava e nada mudava. Enquanto a escolha não for honrada de ponta
 * a ponta, um rótulo verdadeiro vale mais que um controle falso.
 */
const ModelBadge: FC<Props> = ({ info, model, catalog, variant = 'header', className = '' }) => {
  const resolved = info ?? describeModel(model, catalog);
  if (!resolved) return null;

  const title = resolved.provider
    ? `${resolved.full}. ${ONDE_RODA}`
    : `Deployment "${resolved.id}". A plataforma não registrou o modelo por trás dele. ${ONDE_RODA}`;

  if (variant === 'inline') {
    return (
      <span
        data-testid="model-badge"
        title={title}
        className={`inline-flex items-center gap-1 text-2xs text-content-tertiary ${className}`}
      >
        <span className="material-symbols-outlined text-[13px] leading-none" aria-hidden>
          auto_awesome
        </span>
        <span>{resolved.full}</span>
      </span>
    );
  }

  return (
    <span
      data-testid="model-badge"
      title={title}
      className={`inline-flex items-center gap-1.5 shrink-0 rounded-full border border-surface-border bg-surface-secondary px-2 py-1 text-2xs text-content-secondary ${className}`}
    >
      <span className="material-symbols-outlined text-[14px] leading-none" aria-hidden>
        auto_awesome
      </span>
      <span className="truncate max-w-[160px]">{resolved.full}</span>
    </span>
  );
};

export default ModelBadge;
